import { isAuthenticated } from './stores';
class ApiClient {
    get accessToken() {
        if (this._accessToken === null) {
            this._accessToken = localStorage.getItem('access_token');
        }
        return this._accessToken;
    }
    set accessToken(token) {
        this._accessToken = token;
        if (token) {
            localStorage.setItem('access_token', token);
        }
        else {
            localStorage.removeItem('access_token');
        }
    }
    constructor(baseUrl) {
        var _a;
        this._accessToken = null;
        let defaultBaseUrl = 'http://localhost:8123';
        if ((_a = window.injectedEnv) === null || _a === void 0 ? void 0 : _a.FRONTEND_SERVICE_URL) {
            defaultBaseUrl = window.injectedEnv.FRONTEND_SERVICE_URL;
        }
        const finalBaseUrl = baseUrl || defaultBaseUrl;
        this.warnIfBaseUrlIsInvalid(finalBaseUrl);
        this.baseUrl = finalBaseUrl;
        if (this.accessToken) {
            isAuthenticated.set(true);
            this.handleTokenExpiryLogout();
        }
    }
    warnIfBaseUrlIsInvalid(baseUrl) {
        try {
            new URL(baseUrl); // throws if string is not a valid URL
        }
        catch (urlConversionError) {
            console.error("Service URL misconfigured.");
        }
    }
    getAuthHeaders() {
        const headers = {
            'Content-Type': 'application/json',
        };
        if (this.accessToken) {
            headers['Authorization'] = `Bearer ${this.accessToken}`;
        }
        return headers;
    }
    async request(path, options) {
        const response = await fetch(`${this.baseUrl}${path}`, Object.assign({ headers: Object.assign(Object.assign({}, this.getAuthHeaders()), options === null || options === void 0 ? void 0 : options.headers) }, options));
        if (!response.ok) {
            // If we get a 401 and we have a token, it might be expired
            if (response.status === 401 && this.accessToken) {
                this.logout();
            }
            // Try to extract error message from response body
            let errorMessage = `HTTP error! status: ${response.status}`;
            try {
                const errorData = await response.json();
                if (errorData.detail) {
                    errorMessage = errorData.detail;
                }
            }
            catch (e) {
                // If we can't parse the error response, fall back to the default message
            }
            throw new Error(errorMessage);
        }
        if (response.status === 204) {
            return undefined;
        }
        return response.json();
    }
    // Authentication methods
    async login(username, password) {
        const formData = new FormData();
        formData.append('username', username);
        formData.append('password', password);
        const response = await fetch(`${this.baseUrl}/token`, {
            method: 'POST',
            body: formData,
        });
        if (!response.ok) {
            let errorMessage = 'Login failed';
            try {
                const errorData = await response.json();
                if (errorData.detail) {
                    errorMessage = errorData.detail;
                }
            }
            catch (e) {
                // If we can't parse the error response, fall back to the default message
            }
            throw new Error(errorMessage);
        }
        const loginResponse = await response.json();
        this.accessToken = loginResponse.access_token;
        isAuthenticated.set(true);
        this.handleTokenExpiryLogout();
        return loginResponse;
    }
    logout() {
        this.accessToken = null;
        isAuthenticated.set(false);
    }
    handleTokenExpiryLogout() {
        const token = localStorage.getItem('access_token');
        if (!token)
            return;
        let payload;
        try {
            payload = JSON.parse(atob(token.split('.')[1]));
        }
        catch (e) {
            console.error("Failed to parse token payload. Logging out.");
            this.logout();
            return;
        }
        if (!payload || typeof payload.exp !== 'number') {
            return;
        }
        const nowSec = Date.now() / 1000;
        const msUntilExpiry = (payload.exp - nowSec) * 1000;
        if (msUntilExpiry <= 0) {
            console.log("Token expired, logging out immediately.");
            this.logout();
        }
        else if (msUntilExpiry > 2147483647) {
            console.warn("Logout too far in the future, not scheduling logout.");
            return;
        }
        else {
            setTimeout(() => {
                console.log("Token expired, logging out.");
                this.logout();
            }, msUntilExpiry);
        }
    }
    async getOrders() {
        return this.request(`/orders`);
    }
    async getRandomName() {
        return this.request(`/random-name`);
    }
    async acceptOrderAs(id, user) {
        return this.request(`/orders/${id}/accept`, {
            method: "POST",
            body: JSON.stringify({ attendant: user }),
        });
    }
    async unacceptOrder(id) {
        return this.request(`/orders/${id}/accept`, {
            method: "DELETE",
        });
    }
    async finishOrder(id) {
        return this.request(`/orders/${id}/done`, {
            method: "POST",
        });
    }
}
ApiClient.key = {};
export const apiClient = new ApiClient();
